import { useMemo } from "react";
import {
  SessionEvent,
  AnySessionPart,
  SessionStatus,
  SessionPartKind,
  SessionPartWithContext,
  SessionEventKind,
} from "../session.types";
import { sortEventsByTimestamp } from "../session.utils";
import { SessionPartConfig } from "@/mds/components/sessions/session-parts/session-part.types";

export enum SessionItemKind {
  Part = "part",
  ReasoningGroup = "reasoning-group",
}

export interface SessionItem {
  id: string;
  kind: SessionItemKind;
  data: SessionPartWithContext | SessionPartWithContext[] | null;
}

export interface SessionItemContext {
  status: SessionStatus | null;
  config: {
    openLinksInNewTab: boolean;
  };
  // ID of the last reasoning group in the current list
  lastReasoningGroupId: string | null;
  // ID of the very last item in the list
  lastItemId: string | null;
}

const getPartsFromEvent = ({
  event,
  config,
}: {
  event: SessionEvent;
  config: SessionPartConfig;
}): SessionPartWithContext[] => {
  const parts = (event.data.value?.parts as AnySessionPart[]) || [];
  return parts.map((part, index) => ({
    part,
    context: {
      eventKind: event.data.kind as SessionEventKind,
      eventId: event.id,
      isLastInGroup: index === parts.length - 1,
      config,
    },
  }));
};

const isReasoningPart = (partWithContext: SessionPartWithContext): boolean => {
  return partWithContext.part.kind === SessionPartKind.ReasoningV1;
};

const isMessagePart = (partWithContext: SessionPartWithContext): boolean => {
  return (
    partWithContext.part.kind === SessionPartKind.MarkdownV1 ||
    partWithContext.part.kind === SessionPartKind.WebClipV1 ||
    partWithContext.part.kind === SessionPartKind.ArtifactV1
  );
};

export const useSessionItems = ({
  sessionEvents,
  config,
}: {
  sessionEvents: SessionEvent[];
  config: SessionPartConfig;
}): SessionItem[] => {
  return useMemo(() => {
    const sortedEvents = sortEventsByTimestamp(sessionEvents);
    const items: SessionItem[] = [];
    let currentReasoningGroup: SessionPartWithContext[] = [];
    let itemIdCounter = 0;

    const flushReasoningGroup = () => {
      if (currentReasoningGroup.length === 0) return;

      // TODO: Use backend-driven IDs for reasoning groups
      items.push({
        id: `${SessionItemKind.ReasoningGroup}-${itemIdCounter++}`,
        kind: SessionItemKind.ReasoningGroup,
        data: [...currentReasoningGroup],
      });

      currentReasoningGroup = [];
    };

    sortedEvents.forEach(event => {
      const partsWithContext = getPartsFromEvent({ event, config });

      partsWithContext.forEach(partWithContext => {
        if (isReasoningPart(partWithContext)) {
          currentReasoningGroup.push(partWithContext);
        } else if (isMessagePart(partWithContext)) {
          // If part is message content, flush reasoning group and add as individual part
          flushReasoningGroup();

          items.push({
            id: `part-${partWithContext.context.eventId}-${itemIdCounter++}`,
            kind: SessionItemKind.Part,
            data: partWithContext,
          });
        }
      });
    });

    // Flush any remaining reasoning group
    flushReasoningGroup();

    return items;
  }, [config, sessionEvents]);
};
